// src/components/resources/tools/generators/AdSafeZone/AdSafeZoneChecker.tsx

/**
 * @fileoverview Ad Safe Zone Checker
 * @description Upload a vertical ad creative and preview exactly where each
 * platform's UI (top chrome, caption/CTA overlay, and the right-hand action
 * rail) will sit over it on TikTok, Meta Stories/Reels, and YouTube Shorts —
 * before the ad is trafficked. Everything runs client-side: the creative is
 * read with FileReader and never leaves the browser.
 *
 * Design decisions this component is deliberately built around (each one maps
 * to a correctness defect found in the abandoned first attempt, PR #327):
 *
 * 1. **No silent cropping.** A non-9:16 upload is letterboxed (`fit`) by
 *    default and the mismatch is called out in an explicit notice; the
 *    center-crop simulation (`fill`) is a labeled, opt-in toggle — the tool
 *    never quietly crops the user's creative and pretends it fits.
 * 2. **Rail-aware safe math.** The reported "largest fully safe rectangle"
 *    comes from `computeSpecSafeRect`, which subtracts side margins AND rail
 *    blocks — TikTok reads 660×1020 on the 1080×1920 canvas, not the full
 *    frame width and not the 840px band-only width between the side margins
 *    (`r3564178618`).
 * 3. **Upload guards.** ≤ 10 MB (shared generator cap), then the whole file
 *    is read and validated from BYTES: magic-byte format detection
 *    (PNG/JPG/WebP — `File.type` is advisory; browsers may report it empty),
 *    a full-buffer dimension parse that rejects malformed files and 48 MP+
 *    pixel bombs BEFORE any decode, plus a decode-time backstop. A monotonic
 *    upload-sequence token discards stale async work, so replacing a file
 *    mid-pipeline can never commit the wrong creative (`r3564178614`).
 * 4. **Crawlable content is never tab-gated.** The full four-platform
 *    reference table + sources renders unconditionally in the initial markup
 *    (this component is SSR'd by the generator route's `dynamic()` import),
 *    so every platform's numbers are in first-paint HTML — the platform
 *    toggle only affects the visual preview.
 * 5. **Sourced numbers only.** All margins live in `safe-zones.ts` with a
 *    citation per platform (official Meta percentages; TikTok/Google safe-zone
 *    template artifacts) and honest caveats where a template varies.
 *
 * Presentation: the control column stays terse — short labels, stat cells,
 * conditional controls (the Fit/Fill toggle and aspect notice only appear for
 * a non-9:16 upload), and the source/caveat prose disclosed behind a native
 * `<details>`. The long-form reference table, methodology, and FAQs live
 * below the tool as the crawlable copy (owner direction, `c4946086078`:
 * progressive disclosure over walls of text; the SSR surfaces keep the SEO
 * content).
 *
 * Performance: the preview is static positioned `<div>`s and one `<img>` —
 * no canvas work, no per-frame JS, no animation. The only heavy path is the
 * explicit PNG export (html2canvas via the shared `captureAndDownload`),
 * which is warmed off the click path by `useExport`.
 *
 * @module AdSafeZoneChecker
 */

'use client'

import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { toast } from 'sonner'
import { ChevronDownIcon, LockClosedIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { MAX_UPLOAD_BYTES } from '../shared/uploadLimits'
import { detectImageFormat, readImageDimensionsFromHeader } from './image-dimensions'
import {
  MAX_IMAGE_PIXELS,
  PLATFORM_SAFE_ZONES,
  REFERENCE_CANVAS,
  combinedMargins,
  computeSafeRect,
  computeSpecSafeRect,
  conservativeMargins,
  describeAspect,
  fitRect,
  isNineBySixteen,
  overlayZones,
  safeAreaFraction,
  specSafeAreaFraction,
  specZones,
  toReferencePx,
  type FitMode,
  type PlatformId,
  type SafeZoneMargins,
} from './safe-zones'

export interface AdSafeZoneCheckerProps {
  /** Attribution slug for engagement analytics (variant-ready, house pattern). */
  trackingSlug?: string
}

interface UploadedCreative {
  dataUrl: string
  width: number
  height: number
  name: string
}

type Selection = PlatformId | 'all'

/** Combined view: strictest union of every platform's covered sides. */
const COMBINED = combinedMargins(PLATFORM_SAFE_ZONES)
const COMBINED_LABELS = {
  top: 'Covered on at least one platform (top)',
  bottom: 'Covered on at least one platform (bottom)',
  left: 'Covered on at least one platform (left)',
  right: 'Covered on at least one platform (right)',
}

/**
 * File-picker filter hint (MIME types + extensions, so pickers that resolve
 * by extension still filter when the OS reports no MIME type). The REAL
 * format gate is `detectImageFormat` over the file's magic bytes — never
 * `File.type`, which browsers may report as an empty string for a perfectly
 * valid PNG/JPG/WebP. Mirrors the resource config's `allowedFileTypes`.
 */
const ACCEPT_ATTR = 'image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp'

const pct = (fraction: number): string => {
  const value = fraction * 100
  return `${Number.isInteger(Math.round(value * 10) / 10) ? Math.round(value) : (Math.round(value * 10) / 10).toFixed(1)}%`
}

const SOURCE_KIND_LABEL = {
  'official-percent': 'Official spec',
  'official-template': 'Official template',
} as const

/** Compact px + % cell used in the readout stat grid. */
function StatCell({ label, px, fraction }: { label: string; px: number; fraction: number }): JSX.Element {
  return (
    <div className="rounded-xl bg-white px-3 py-2 dark:bg-slate-900/70">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-50">
        {px}px <span className="font-normal text-slate-500 dark:text-slate-400">({pct(fraction)})</span>
      </dd>
    </div>
  )
}

export function AdSafeZoneChecker({
  trackingSlug = 'ad-safe-zone-checker',
}: AdSafeZoneCheckerProps): JSX.Element {
  const [selection, setSelection] = useState<Selection>('tiktok')
  const [fitMode, setFitMode] = useState<FitMode>('fit')
  const [creative, setCreative] = useState<UploadedCreative | null>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Monotonic token for the async upload pipeline: each selection (and each
  // removal) bumps it, and the FileReader/Image callbacks bail when their
  // captured token is no longer current. Without this, replacing file A with
  // file B while A is still decoding lets whichever `onload` fires LAST win
  // the state — the preview/export could show A's pixels under B's filename
  // (`r3564178614`).
  const uploadSeqRef = useRef(0)
  const { isExporting, runExport } = useExport()
  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])

  const activeSpec = selection === 'all' ? null : PLATFORM_SAFE_ZONES.find((s) => s.id === selection) ?? null
  const activeMargins: SafeZoneMargins = activeSpec ? activeSpec.margins : COMBINED
  // Cheap enough to compute per render (a handful of tiny objects); the React
  // Compiler memoizes it automatically — a manual useMemo here defeated its
  // memoization inference (react-compiler lint error).
  const zones = activeSpec ? specZones(activeSpec) : overlayZones(COMBINED, COMBINED_LABELS)
  // The dashed outline / reported "safe area" is the largest fully-safe
  // RECTANGLE — rail-aware (see conservativeMargins), so TikTok's reported
  // width excludes the rail instead of claiming the full frame width.
  const outlineMargins: SafeZoneMargins = activeSpec ? conservativeMargins(activeSpec) : COMBINED
  const safeRect = activeSpec ? computeSpecSafeRect(activeSpec) : computeSafeRect(COMBINED)
  // Two DISTINCT metrics, never mixed on one line (`r3564434263`): the % shown
  // next to the safe rectangle is the RECTANGLE's own share of the canvas;
  // the L-shaped total uncovered area (rectangle + the notch above the rail)
  // is labeled separately and only rendered when a rail makes them differ.
  const rectPct =
    (safeRect.width * safeRect.height) / (REFERENCE_CANVAS.width * REFERENCE_CANVAS.height)
  const totalUncoveredPct = activeSpec ? specSafeAreaFraction(activeSpec) : safeAreaFraction(COMBINED)

  const trackInteraction = useCallback(
    (interactionType: string, params: Record<string, unknown> = {}): void => {
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: { tool_type: 'generator', interaction_type: interactionType, ...params },
      })
    },
    [getDeltaEngagementMs, trackingSlug],
  )

  const selectPlatform = useCallback(
    (next: Selection): void => {
      setSelection(next)
      trackInteraction('select_platform', { platform: next })
    },
    [trackInteraction],
  )

  const handleUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const input = event.target
      const file = input.files?.[0]
      if (!file) return
      // New selection supersedes any in-flight decode immediately.
      const seq = ++uploadSeqRef.current
      const isCurrent = (): boolean => seq === uploadSeqRef.current
      const rejectOverBudget = (megapixels: number): void => {
        toast.error(
          `That image is ${Math.round(megapixels)} MP — too large to preview reliably (max 48 MP). Export at delivery size (1080×1920) and try again.`,
        )
        input.value = ''
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error('Image is too large (max 10 MB). Export a smaller version and try again.')
        input.value = ''
        return
      }
      const startRead = (): void => {
        const reader = new FileReader()
        reader.onerror = () => {
          if (!isCurrent()) return
          toast.error('Could not read that image. Please try another file.')
          input.value = ''
        }
        reader.onload = () => {
          if (!isCurrent()) return
          const dataUrl = typeof reader.result === 'string' ? reader.result : null
          if (!dataUrl) return
          // Decode off-DOM so the dimensions are confirmed before the bitmap
          // enters the preview. This is the BACKSTOP pixel-budget check — the
          // primary check parses the file header before any decode (below).
          const probe = new Image()
          probe.onload = () => {
            if (!isCurrent()) return // a newer selection superseded this decode
            const { naturalWidth: width, naturalHeight: height } = probe
            if (width <= 0 || height <= 0) {
              toast.error('Could not decode that image. Please try another file.')
              input.value = ''
              return
            }
            if (width * height > MAX_IMAGE_PIXELS) {
              rejectOverBudget((width * height) / 1_000_000)
              return
            }
            setCreative({ dataUrl, width, height, name: file.name })
            // Cropping is opt-in PER ASSET: a Fill choice made for one
            // creative must not silently carry over to the next, or a fresh
            // upload opens center-cropped while the copy promises the
            // letterbox default (`r3564434261`).
            setFitMode('fit')
            // Anonymous usage analytics (format/byte size/pixel dimensions —
            // never the image or its filename), per the owner's first-party
            // analytics posture (docs/analytics/first-party-analytics-posture.md).
            // The on-page privacy copy and FAQ state exactly this split
            // (`r3564283753`): the CREATIVE never leaves the browser; usage
            // metadata is ordinary first-party analytics.
            trackInteraction('upload_creative', {
              file_type: file.type,
              file_size: file.size,
              image_width: width,
              image_height: height,
            })
            // Clear the input so re-selecting the SAME file after Remove
            // still fires `change` in real browsers (`r3564283751`). Error
            // paths already clear it; the success path must too.
            input.value = ''
          }
          probe.onerror = () => {
            if (!isCurrent()) return
            toast.error('Could not decode that image. Please try another file.')
            input.value = ''
          }
          probe.src = dataUrl
        }
        reader.readAsDataURL(file)
      }
      // PRIMARY validation gate (`r3564283755` + the empty-MIME follow-up):
      // uploads are capped at 10 MB, so read the WHOLE file (cheap next to a
      // decode) and validate from BYTES before anything reaches the image
      // decoder:
      //   1. Magic-byte format detection — `File.type` is advisory only
      //      (browsers may report an empty string for a valid PNG/JPG/WebP,
      //      and a mislabeled MIME lies the other way).
      //   2. Dimension parse over the complete buffer — in a well-formed
      //      file of a supported format the marker is always findable, so an
      //      unparseable result means the file is malformed for our set:
      //      reject with a precise message, never "decode and hope".
      //   3. Pixel budget on the parsed dimensions — a compressed pixel bomb
      //      fails here, before the decoder pays the cost the `probe.onload`
      //      backstop can only observe after the fact.
      const validateReader = new FileReader()
      validateReader.onload = () => {
        if (!isCurrent()) return
        const result = validateReader.result
        if (!(result instanceof ArrayBuffer)) {
          toast.error('Could not read that image. Please try another file.')
          input.value = ''
          return
        }
        const bytes = new Uint8Array(result)
        if (!detectImageFormat(bytes)) {
          toast.error('Unsupported format. Upload a PNG, JPG, or WebP creative.')
          input.value = ''
          return
        }
        const dims = readImageDimensionsFromHeader(bytes)
        if (!dims) {
          toast.error(
            'Could not read that image’s dimensions — the file looks damaged. Re-export it as PNG, JPG, or WebP and try again.',
          )
          input.value = ''
          return
        }
        if (dims.width * dims.height > MAX_IMAGE_PIXELS) {
          rejectOverBudget((dims.width * dims.height) / 1_000_000)
          return
        }
        startRead()
      }
      validateReader.onerror = () => {
        if (!isCurrent()) return
        toast.error('Could not read that image. Please try another file.')
        input.value = ''
      }
      validateReader.readAsArrayBuffer(file)
    },
    [trackInteraction],
  )

  const removeCreative = useCallback((): void => {
    // Bump the token so an in-flight decode can't resurrect a removed image.
    uploadSeqRef.current += 1
    setCreative(null)
    // Clear the input HERE too, not only in the pipeline callbacks: a stale
    // callback bails at its isCurrent() guard WITHOUT resetting the value, so
    // removing mid-replacement would otherwise leave the superseded file
    // selected and a same-file re-selection would fire no `change`
    // (`r3564389511`). The reset is unconditional at every terminal point —
    // success, error, and remove.
    if (fileInputRef.current) fileInputRef.current.value = ''
    // Back to the letterbox default for whatever is uploaded next
    // (`r3564434261`).
    setFitMode('fit')
  }, [])

  // No manual useCallback: the React Compiler memoizes this automatically
  // (and its identity isn't a correctness dependency for ExportButton).
  const exportPng = (): Promise<void> =>
    runExport(async (beginCapture) => {
      if (!previewRef.current) {
        toast.error('Preview not found. Please ensure the preview is visible.')
        return
      }
      beginCapture()
      await captureAndDownload({
        element: previewRef.current,
        format: 'png',
        filename: `${selection}-safe-zone-preview`,
        trackingSlug,
        engagementMs: getTotalEngagementMs(),
        extraParams: { platform: selection, fit_mode: fitMode, has_creative: creative !== null },
        onSuccess: () => toast.success('Safe-zone preview exported!'),
        onError: () => toast.error('Failed to export the preview. Please try again.'),
      })
    })

  // Creative placement on the 9:16 stage, in percentages of the stage box so
  // the same math holds at any rendered size.
  const creativeRect = creative
    ? fitRect(creative.width, creative.height, 1, REFERENCE_CANVAS.height / REFERENCE_CANVAS.width, fitMode)
    : null
  const stageAspectHeight = REFERENCE_CANVAS.height / REFERENCE_CANVAS.width
  const creativeIs916 = creative ? isNineBySixteen(creative.width, creative.height) : true

  const toggleClasses = (active: boolean): string =>
    clsx(
      'rounded-xl px-3 py-2 text-center text-sm font-medium transition',
      active
        ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-300'
        : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white',
    )

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-1 sm:px-0">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        {/* ── Controls ─────────────────────────────────────────────── */}
        <section
          aria-label="Safe zone checker controls"
          className="flex-1 rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-2xl dark:border-slate-800/70 dark:bg-slate-900/70"
        >
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Check your creative</h2>

          {/* Upload */}
          <div className="mt-4">
            <label className="group flex cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-5 transition hover:border-indigo-400 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800/60 dark:hover:border-indigo-400">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-700 group-hover:text-indigo-600 dark:text-slate-200 dark:group-hover:text-indigo-300">
                <PhotoIcon className="h-5 w-5" aria-hidden="true" />
                {creative ? 'Replace creative' : 'Upload creative'}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">PNG · JPG · WebP · up to 10 MB</span>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                aria-label="Upload ad creative image"
                className="sr-only"
                onChange={handleUpload}
              />
            </label>
            <div className="mt-1.5 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
              {creative ? (
                <>
                  <span className="truncate">
                    {creative.name} — {creative.width}×{creative.height}px ({describeAspect(creative.width, creative.height)})
                  </span>
                  <button
                    type="button"
                    onClick={removeCreative}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Remove
                  </button>
                </>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Your image never leaves this browser.
                </span>
              )}
            </div>
          </div>

          {/* Aspect-mismatch notice — explicit, never silent; only rendered
              when it applies (progressive disclosure). */}
          {creative && !creativeIs916 && (
            <div
              role="status"
              className="mt-4 rounded-2xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-200"
            >
              <strong className="font-semibold">
                {describeAspect(creative.width, creative.height)} creative in a 9:16 placement.
              </strong>{' '}
              {fitMode === 'fit'
                ? 'Shown letterboxed — nothing is hidden. Fill simulates the platform’s center-crop.'
                : 'Fill simulates the platform’s center-crop — overflow outside the frame is what gets cut off.'}
            </div>
          )}

          {/* Placement toggle */}
          <fieldset className="mt-5">
            <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">Placement</legend>
            <div className="mt-2 grid grid-cols-2 gap-1.5 rounded-2xl bg-slate-100 p-1.5 sm:grid-cols-5 dark:bg-slate-800/60">
              {PLATFORM_SAFE_ZONES.map((spec) => (
                <button
                  key={spec.id}
                  type="button"
                  onClick={() => selectPlatform(spec.id)}
                  aria-pressed={selection === spec.id}
                  className={toggleClasses(selection === spec.id)}
                >
                  {spec.shortName}
                </button>
              ))}
              <button
                type="button"
                onClick={() => selectPlatform('all')}
                aria-pressed={selection === 'all'}
                className={toggleClasses(selection === 'all')}
              >
                All (strictest)
              </button>
            </div>
          </fieldset>

          {/* Fit mode — only relevant (and only shown) for a non-9:16 upload */}
          {creative && !creativeIs916 && (
            <fieldset className="mt-4">
              <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">Framing</legend>
              <div className="mt-2 inline-grid grid-cols-2 gap-1.5 rounded-2xl bg-slate-100 p-1.5 dark:bg-slate-800/60">
                <button
                  type="button"
                  onClick={() => setFitMode('fit')}
                  aria-pressed={fitMode === 'fit'}
                  className={toggleClasses(fitMode === 'fit')}
                >
                  Fit (letterbox)
                </button>
                <button
                  type="button"
                  onClick={() => setFitMode('fill')}
                  aria-pressed={fitMode === 'fill'}
                  className={toggleClasses(fitMode === 'fill')}
                >
                  Fill (simulate crop)
                </button>
              </div>
            </fieldset>
          )}

          {/* Active-selection readout — compact stat cells; the prose lives in
              the disclosure below and in the SSR reference table. */}
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                {activeSpec ? activeSpec.name : 'All platforms — strictest combined'}
              </h3>
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                {activeSpec ? SOURCE_KIND_LABEL[activeSpec.source.kind] : 'Per-side maximum'}
              </span>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
              <StatCell label="Top" px={toReferencePx(activeMargins.top, REFERENCE_CANVAS.height)} fraction={activeMargins.top} />
              <StatCell label="Bottom" px={toReferencePx(activeMargins.bottom, REFERENCE_CANVAS.height)} fraction={activeMargins.bottom} />
              <StatCell label="Left" px={toReferencePx(activeMargins.left, REFERENCE_CANVAS.width)} fraction={activeMargins.left} />
              <StatCell label="Right" px={toReferencePx(activeMargins.right, REFERENCE_CANVAS.width)} fraction={activeMargins.right} />
            </dl>
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-300">
              Safe rectangle{' '}
              <strong className="tabular-nums">
                {Math.round(safeRect.width)}×{Math.round(safeRect.height)}px
              </strong>
              {activeSpec?.rail ? ' (clears the rail)' : ''} · {pct(rectPct)} of the frame
            </p>
            {activeSpec?.rail && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                + {activeSpec.rail.label.toLowerCase()}: {toReferencePx(activeSpec.rail.width, REFERENCE_CANVAS.width)}×{toReferencePx(activeSpec.rail.height, REFERENCE_CANVAS.height)}px, lower right · total uncovered area incl. the notch above the rail: {pct(totalUncoveredPct)}
              </p>
            )}

            {/* Progressive disclosure: source + caveat prose on demand.
                Native <details> — keyboard-accessible, and its content is
                still SSR'd into first-paint HTML for crawlers. */}
            <details className="group mt-3 border-t border-slate-200 pt-2.5 dark:border-slate-700">
              <summary className="flex cursor-pointer list-none items-center gap-1 text-xs font-medium text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white [&::-webkit-details-marker]:hidden">
                <ChevronDownIcon aria-hidden="true" className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                Source &amp; notes
              </summary>
              <div className="mt-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {activeSpec ? (
                  <>
                    <a
                      href={activeSpec.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-500 dark:text-indigo-400"
                    >
                      {activeSpec.source.publisher}
                    </a>{' '}
                    — {activeSpec.source.title}.{activeSpec.caveat ? ` ${activeSpec.caveat}` : ''} Margins are shown on the 1080×1920 reference canvas; the safe rectangle already excludes side margins and action rails. The full table for every placement is below the tool.
                  </>
                ) : (
                  'Per-side maximum across all four placements (rail-aware) — a layout inside this rectangle is inside every platform’s safe zone. Sources for each placement are linked in the reference table below the tool.'
                )}
              </div>
            </details>
          </div>

          <div className="mt-5">
            <ExportButton
              isExporting={isExporting}
              onExport={exportPng}
              label="Download annotated PNG"
              className="w-full rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg"
              idleClassName="hover:bg-indigo-500"
              exportingClassName="opacity-80"
            />
          </div>
        </section>

        {/* ── Preview ──────────────────────────────────────────────── */}
        <section aria-label="Safe zone preview" className="mx-auto w-full max-w-[340px] lg:mx-0 lg:w-[340px] lg:shrink-0">
          <div
            ref={previewRef}
            className="relative w-full overflow-hidden rounded-[1.75rem] border border-slate-300 bg-slate-950 shadow-2xl dark:border-slate-700"
            style={{ aspectRatio: `${REFERENCE_CANVAS.width} / ${REFERENCE_CANVAS.height}` }}
          >
            {creative && creativeRect ? (
              // eslint-disable-next-line @next/next/no-img-element -- user-uploaded data URL; next/image can't optimize data URIs and html2canvas needs the raw <img>
              <img
                src={creative.dataUrl}
                alt="Your uploaded ad creative, positioned inside the 9:16 placement frame"
                // max-w-none is load-bearing: Tailwind preflight sets
                // `img { max-width: 100% }`, which clamps the Fill-mode
                // overflow width (e.g. 316% for a 16:9 creative) to the frame
                // and shoves the image fully off-canvas at its negative left —
                // the creative silently vanished in Fill mode without it
                // (caught by screenshot verification).
                className="absolute max-w-none"
                style={{
                  left: `${creativeRect.x * 100}%`,
                  top: `${(creativeRect.y / stageAspectHeight) * 100}%`,
                  width: `${creativeRect.width * 100}%`,
                  height: `${(creativeRect.height / stageAspectHeight) * 100}%`,
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 p-6 text-center text-sm text-slate-400">
                Upload a creative to preview it here — the overlays below show what each platform’s UI covers either way.
              </div>
            )}

            {/* UI overlay zones */}
            {zones.map((zone) => (
              <div
                key={`${zone.side}-${zone.x.toFixed(4)}-${zone.y.toFixed(4)}`}
                aria-hidden="true"
                className={clsx(
                  'pointer-events-none absolute flex items-center justify-center overflow-hidden',
                  zone.side === 'top' || zone.side === 'bottom' ? 'bg-rose-500/40' : 'bg-amber-500/40',
                )}
                style={{
                  left: `${zone.x * 100}%`,
                  top: `${zone.y * 100}%`,
                  width: `${zone.width * 100}%`,
                  height: `${zone.height * 100}%`,
                }}
              >
                {(zone.side === 'top' || zone.side === 'bottom') && (
                  <span className="px-2 text-center text-[10px] font-semibold uppercase tracking-wide text-white/90">
                    {zone.label}
                  </span>
                )}
              </div>
            ))}

            {/* Safe-area outline — the largest fully-safe rectangle (rail-aware) */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute border-2 border-dashed border-emerald-400"
              style={{
                left: `${outlineMargins.left * 100}%`,
                top: `${outlineMargins.top * 100}%`,
                width: `${(1 - outlineMargins.left - outlineMargins.right) * 100}%`,
                height: `${(1 - outlineMargins.top - outlineMargins.bottom) * 100}%`,
              }}
            />
          </div>

          {/* Legend — one short line per color */}
          <ul className="mt-3 space-y-1 text-xs text-slate-600 dark:text-slate-400">
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-3 w-3 shrink-0 rounded-sm bg-rose-500/60" />
              Platform UI (top &amp; bottom)
            </li>
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-3 w-3 shrink-0 rounded-sm bg-amber-500/60" />
              Side margins &amp; action rails
            </li>
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-3 w-3 shrink-0 rounded-sm border-2 border-dashed border-emerald-400" />
              Safe area — keep text, logos &amp; CTAs inside
            </li>
          </ul>
        </section>
      </div>

      {/* ── Reference table — deliberately rendered unconditionally so every
             platform's numbers ship in first-paint HTML (never tab-gated). ── */}
      <section
        aria-label="Safe zone reference table for all platforms"
        className="rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-2xl dark:border-slate-800/70 dark:bg-slate-900/70"
      >
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
          2026 safe-zone margins by platform (1080×1920 canvas)
        </h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Margins that platform UI covers on a 9:16 vertical creative, with the safe area that remains. Every figure links to the platform’s own spec or safe-zone template.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 dark:border-slate-700 dark:text-slate-400">
                <th scope="col" className="py-2 pr-4 font-medium">Placement</th>
                <th scope="col" className="py-2 pr-4 font-medium">Top</th>
                <th scope="col" className="py-2 pr-4 font-medium">Bottom</th>
                <th scope="col" className="py-2 pr-4 font-medium">Left</th>
                <th scope="col" className="py-2 pr-4 font-medium">Right</th>
                <th scope="col" className="py-2 pr-4 font-medium">Safe rectangle</th>
                <th scope="col" className="py-2 font-medium">Source</th>
              </tr>
            </thead>
            <tbody className="text-slate-700 dark:text-slate-300">
              {PLATFORM_SAFE_ZONES.map((spec) => {
                const rect = computeSpecSafeRect(spec)
                return (
                  <tr key={spec.id} className="border-b border-slate-100 align-top dark:border-slate-800">
                    <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900 dark:text-slate-50">
                      {spec.name}
                      {spec.rail && (
                        <span className="mt-0.5 block text-xs font-normal text-slate-500 dark:text-slate-400">
                          + {toReferencePx(spec.rail.width, REFERENCE_CANVAS.width)}×{toReferencePx(spec.rail.height, REFERENCE_CANVAS.height)}px {spec.rail.label.toLowerCase()} (lower right)
                        </span>
                      )}
                    </th>
                    <td className="py-2.5 pr-4 tabular-nums">
                      {toReferencePx(spec.margins.top, REFERENCE_CANVAS.height)}px ({pct(spec.margins.top)})
                    </td>
                    <td className="py-2.5 pr-4 tabular-nums">
                      {toReferencePx(spec.margins.bottom, REFERENCE_CANVAS.height)}px ({pct(spec.margins.bottom)})
                    </td>
                    <td className="py-2.5 pr-4 tabular-nums">
                      {toReferencePx(spec.margins.left, REFERENCE_CANVAS.width)}px ({pct(spec.margins.left)})
                    </td>
                    <td className="py-2.5 pr-4 tabular-nums">
                      {toReferencePx(spec.margins.right, REFERENCE_CANVAS.width)}px ({pct(spec.margins.right)})
                    </td>
                    <td className="py-2.5 pr-4 tabular-nums">
                      {Math.round(rect.width)}×{Math.round(rect.height)}px
                    </td>
                    <td className="py-2.5">
                      <a
                        href={spec.source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-500 dark:text-indigo-400"
                      >
                        {spec.source.publisher}
                      </a>{' '}
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        ({SOURCE_KIND_LABEL[spec.source.kind]})
                      </span>
                    </td>
                  </tr>
                )
              })}
              <tr className="align-top">
                <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900 dark:text-slate-50">
                  All platforms (strictest)
                </th>
                <td className="py-2.5 pr-4 tabular-nums">
                  {toReferencePx(COMBINED.top, REFERENCE_CANVAS.height)}px ({pct(COMBINED.top)})
                </td>
                <td className="py-2.5 pr-4 tabular-nums">
                  {toReferencePx(COMBINED.bottom, REFERENCE_CANVAS.height)}px ({pct(COMBINED.bottom)})
                </td>
                <td className="py-2.5 pr-4 tabular-nums">
                  {toReferencePx(COMBINED.left, REFERENCE_CANVAS.width)}px ({pct(COMBINED.left)})
                </td>
                <td className="py-2.5 pr-4 tabular-nums">
                  {toReferencePx(COMBINED.right, REFERENCE_CANVAS.width)}px ({pct(COMBINED.right)})
                </td>
                <td className="py-2.5 pr-4 tabular-nums">
                  {Math.round(computeSafeRect(COMBINED).width)}×{Math.round(computeSafeRect(COMBINED).height)}px
                </td>
                <td className="py-2.5 text-xs text-slate-500 dark:text-slate-400">
                  Per-side maximum of the rows above
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 space-y-2 text-xs text-slate-500 dark:text-slate-400">
          <p>
            <strong className="font-semibold">Methodology.</strong> Meta figures are the percentages Meta publishes for Stories and Reels ads. TikTok and YouTube Shorts figures are measured directly from each platform’s own published safe-zone template file (TikTok’s “In-Feed — Standard Version” template, annotated on a 720×1280 base, and Google’s vertical safe-zone overlay PNG with an 840×960px safe area at x&nbsp;48, y&nbsp;288 — both downloaded and measured July&nbsp;2026). The “Safe rectangle” column is the largest layout box clear of every band and rail; TikTok’s covered area also grows with caption length and interactive add-ons, so treat its margins as a minimum. Platform UI changes over time; always validate the final creative in each platform’s own preview tool before launch.
          </p>
        </div>
      </section>
    </div>
  )
}
